import { FlexibleReportFormState } from '@flexible-reports/store/slices/report-form/flexible-report-form-slice';
import {
  AccessRequestedAccountId,
  AccountId,
  PeriodPreset,
  ReportConfigurationSchedule,
  ReportConfigurationStatus,
  ReportDeliverySettings,
  ReportFormat,
  ReportingQuery,
  ReportPeriod,
  ScheduleType
} from '@flexible-reports/store/api/subscriptions/types';
import { VirtualColId } from '@urc-ui/pages/landing/subscriptions-table/constants';
import { SUBSCRIPTION_TABLE_COLUMN_ID } from '@flexible-reports/pages/landing/subscriptions-table/column-configurations/column-id';
import { REPORT_FREQUENCY } from '@flexible-reports/pages/landing/subscriptions-table/constants';
import { PRESET_PERIOD } from '@urc-ui/utils/types';
import { FREQUENCY } from '@flexible-reports/utils/schedule-constants';
import { getCoercedTimezone } from '@flexible-reports/utils/timezone/get-coerced-timezone';
import {
  LAST_DAY_OF_MONTH_PRESET,
  periodPresetToApiMap,
  scheduleTypeToApiMap,
  sortDirectionToApiMap,
  sortFieldToApiMap
} from '@flexible-reports/store/api/subscriptions/transformers/mappings';
import { transformTimezoneToApi } from '@flexible-reports/store/api/subscriptions/transformers/timezoneTransformer';
import { REPORT_FORMAT } from '@flexible-reports/utils/report-format-constants';
import { FlexibleReportPeriod, ReportDelivery, ReportFilters, ReportSchedule } from '@flexible-reports/store/slices/types';
import {
  CreateFlexibleReportConfigurationRequest,
  ListFlexibleReportConfigurationsRequest,
  UpdateFlexibleReportConfigurationRequest
} from '@flexible-reports/store/api/subscriptions/api-types';
import { isFormModified } from '@flexible-reports/store/slices/report-form/report-form-slice-utils';
import { SelectedFamily } from '@flexible-reports/store/metadata.types';
import { Sort } from '@urc-ui/store/api/types';
import { AccountIdFilter, ComparisonOperator, Filter } from '@flexible-reports/store/api/types';
import { determineCountryCodesForRequest } from '@flexible-reports/utils/country-utils';
import { isCaracalMetadataDefaultLocaleEnabled, shouldExcludeManagerAccounts } from '@flexible-reports/utils/app-utils';
import { getFormattedLocale } from '@urc-ui/utils/app-utils';
import { logger } from '@flexible-reports/services/logger';

export const buildAccessRequestedAccounts = (linkedAccounts: AccountId[]): AccessRequestedAccountId[] => {
  return linkedAccounts.map((accountId: AccountId) => {
    return {
      ...(accountId.advertiserAccountId && { advertiserAccountId: accountId.advertiserAccountId }),
      ...(accountId.managerAccountId && { managerAccountId: accountId.managerAccountId })
    };
  });
};

export const buildCreateFlexibleReportRequest = (payload: FlexibleReportFormState): CreateFlexibleReportConfigurationRequest => {
  const scheduleType = buildScheduleType(payload.reportSchedule);
  const linkedAccounts = buildAccounts(payload.reportFilters);

  let locale = getFormattedLocale();
  if (isCaracalMetadataDefaultLocaleEnabled()) {
    logger.logInfoMessage(`overriding the locale for create report ${locale} to en-US`);
    locale = 'en-US';
  }

  return {
    accessRequestedAccounts: buildAccessRequestedAccounts(linkedAccounts),
    reportConfigurations: [
      {
        format: payload.reportFormat as ReportFormat,
        linkedAccounts,
        locale,
        name: payload.reportName,
        period: buildReportPeriod(payload.reportPeriod),
        query: buildReportQuery(payload.selectedDimensions, payload.selectedMetrics, payload.reportFilters),
        schedule: buildSchedule(payload.reportSchedule, scheduleType),
        scheduleType: scheduleType,
        status: ReportConfigurationStatus.ACTIVE,
        deliverySettings: buildReportDelivery(payload.reportDelivery),
        emailDeliveryMarketplace: 'US',
        currencyOfView: payload.columnSettings.selectedCurrencyOfView || undefined,
        ...(payload.reportPeriod.reportingTimezone && { timeZoneMode: payload.reportPeriod.reportingTimezone })
      }
    ]
  };
};

export const buildUpdateFlexibleReportRequest = (payload: FlexibleReportFormState): UpdateFlexibleReportConfigurationRequest => {
  if (!payload.reportConfigurationId) {
    throw new Error('Report Configuration Id is missing in Form State while building Update Request');
  }
  const modificationState = payload.formModificationState;
  const isFormEdited = isFormModified(modificationState);

  if (!isFormEdited) {
    throw new Error('Form is not in modified state while building Update Request');
  }

  const linkedAccounts = buildAccounts(payload.reportFilters);
  const scheduleType = buildScheduleType(payload.reportSchedule);
  const removeManagerAccounts = shouldExcludeManagerAccounts() && payload.linkedManagerAccountIds.length > 0;

  return {
    accessRequestedAccounts: buildAccessRequestedAccounts(linkedAccounts),
    reportConfigurations: [
      {
        reportConfigurationId: payload.reportConfigurationId,
        ...(modificationState.isReportNameModified && { name: payload.reportName }),
        ...(modificationState.isScheduleModified && {
          scheduleType,
          schedule: buildSchedule(payload.reportSchedule, scheduleType)
        }),
        ...(modificationState.isReportPeriodModified && {
          period: buildReportPeriod(payload.reportPeriod),
          ...(payload.reportPeriod.reportingTimezone && { timeZoneMode: payload.reportPeriod.reportingTimezone })
        }),
        ...(modificationState.isDeliveryModified && {
          deliverySettings: buildReportDelivery(payload.reportDelivery)
        }),
        ...(removeManagerAccounts && {
          linkedAccounts
        }),
        ...(modificationState.areFiltersModified && {
          linkedAccounts,
          query: buildReportQuery(payload.selectedDimensions, payload.selectedMetrics, payload.reportFilters)
        }),
        ...(modificationState.areColumnsModified && {
          query: buildReportQuery(payload.selectedDimensions, payload.selectedMetrics, payload.reportFilters)
        }),
        ...(modificationState.isCurrencyOfViewModified && {
          currencyOfView: payload.columnSettings.selectedCurrencyOfView || null
        })
      }
    ]
  };
};

export const buildAccounts = (reportFilters: ReportFilters): AccountId[] => {
  return [
    ...reportFilters.managerAccountIds.map((managerAccountId: string) => ({ managerAccountId })),
    ...reportFilters.advertiserAccountIds.map((advertiserAccountId: string) => ({ advertiserAccountId }))
  ];
};

const buildCreateReportFilters = (reportFilters: ReportFilters): Filter | null => {
  const conditions = [!reportFilters.useAllCampaigns, reportFilters.countries.length > 0, reportFilters.productTypes.length > 0];
  const shouldUseAndFilter = conditions.filter(Boolean).length >= 2;
  if (shouldUseAndFilter) {
    return buildAndFilter(reportFilters);
  }

  if (!reportFilters.useAllCampaigns) {
    return filterOnCampaigns(reportFilters);
  }

  if (reportFilters.countries.length > 0) {
    return filterOnCampaignCountry(reportFilters);
  }

  if (reportFilters.productTypes.length > 0) {
    return filterOnProductType(reportFilters);
  }

  return null;
};

const buildAndFilter = (reportFilters: ReportFilters): Filter => {
  const filters: Filter[] = [];
  if (!reportFilters.useAllCampaigns) {
    filters.push(filterOnCampaigns(reportFilters));
  }

  if (reportFilters.countries.length > 0) {
    filters.push(filterOnCampaignCountry(reportFilters));
  }

  if (reportFilters.productTypes.length > 0) {
    filters.push(filterOnProductType(reportFilters));
  }

  return {
    and: {
      filters
    }
  };
};

const filterOnCampaigns = (reportFilters: ReportFilters): Filter => {
  return {
    on: {
      field: 'campaign.id',
      comparisonOperator: ComparisonOperator.IN,
      not: false,
      values: reportFilters.campaignIds
    }
  };
};

const filterOnCampaignCountry = (reportFilters: ReportFilters): Filter => {
  return {
    on: {
      field: 'campaign.country',
      comparisonOperator: ComparisonOperator.IN,
      not: false,
      values: determineCountryCodesForRequest(reportFilters.countries)
    }
  };
};

const filterOnProductType = (reportFilters: ReportFilters): Filter => {
  return {
    on: {
      field: 'adProduct.value',
      comparisonOperator: ComparisonOperator.IN,
      not: false,
      values: reportFilters.productTypes
    }
  };
};

export const buildReportQuery = (
  selectedDimensions: SelectedFamily[],
  selectedMetrics: SelectedFamily[],
  reportFilters?: ReportFilters
): ReportingQuery => {
  const removeParentSelection = selectedMetrics.filter(item => item.id.startsWith('metric.'));
  return {
    fields: [
      ...selectedDimensions.flatMap(dimension =>
        dimension.attributes && dimension.attributes.length === 0 ? dimension.id : dimension.attributes
      ),
      ...removeParentSelection.flatMap(metric => (metric.attributes && metric.attributes.length === 0 ? metric.id : metric.attributes))
    ],
    ...(reportFilters &&
      (reportFilters.countries.length > 0 || reportFilters.campaignIds.length > 0 || reportFilters.productTypes.length > 0) && {
        filter: buildCreateReportFilters(reportFilters)
      })
  } as ReportingQuery;
};

export const buildReportPeriod = (reportPeriod: FlexibleReportPeriod | null): ReportPeriod => {
  if (reportPeriod === null) {
    throw new Error('Report period is null while building Api Request');
  }
  if (PRESET_PERIOD.CUSTOM === reportPeriod?.preset) {
    if (!reportPeriod.start) {
      throw new Error('Report period start date is missing for custom period');
    }
    if (!reportPeriod.end) {
      throw new Error('Report period end date is missing for custom period');
    }
    return {
      datePeriod: {
        startDate: reportPeriod.start,
        endDate: reportPeriod.end
      }
    };
  }

  const periodPreset = periodPresetToApiMap[reportPeriod.preset] || reportPeriod.preset;
  if (!(periodPreset in PeriodPreset)) {
    throw new Error('Invalid Report Period Preset');
  }
  return {
    reportConfigurationPresetPeriod: {
      preset: periodPreset as PeriodPreset
    }
  };
};
export const buildScheduleType = (reportSchedule: ReportSchedule): ScheduleType => {
  if (reportSchedule.frequency === FREQUENCY.ONCE) {
    return scheduleTypeToApiMap[reportSchedule.onceDateOption as string];
  }
  return scheduleTypeToApiMap[reportSchedule.frequency];
};

export const buildSchedule = (reportSchedule: ReportSchedule, scheduleType: ScheduleType): ReportConfigurationSchedule => {
  if (scheduleType === ScheduleType.NOW) {
    return buildNowSchedule();
  }
  if (scheduleType === ScheduleType.FUTURE) {
    return buildFutureSchedule(reportSchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_WEEKLY) {
    return buildWeeklySchedule(reportSchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_DAILY) {
    return buildDailySchedule(reportSchedule);
  }
  if (scheduleType === ScheduleType.RECURRING_MONTHLY) {
    return buildMonthlySchedule(reportSchedule);
  }
  throw new Error('Invalid Schedule Type');
};

const buildNowSchedule = (): ReportConfigurationSchedule => {
  return {
    nowSchedule: {
      timeZone: transformTimezoneToApi(getCoercedTimezone())
    }
  };
};

const buildFutureSchedule = (reportSchedule: ReportSchedule): ReportConfigurationSchedule => {
  if (!reportSchedule.timezone) {
    throw new Error('Timezone is not set for Future Schedule');
  }
  if (!reportSchedule.runDate) {
    throw new Error('Run Date is not set for Future Schedule');
  }

  return {
    futureSchedule: {
      hourOfDay: reportSchedule.hour ?? 0,
      timeZone: transformTimezoneToApi(reportSchedule.timezone),
      runDate: reportSchedule.runDate
    }
  };
};

const buildDailySchedule = (reportSchedule: ReportSchedule): ReportConfigurationSchedule => {
  if (!reportSchedule.timezone) {
    throw new Error('Timezone is not set for Daily Schedule');
  }
  if (!reportSchedule.endDate) {
    throw new Error('End Date is not set for Daily Schedule');
  }

  return {
    recurringDailySchedule: {
      hourOfDay: reportSchedule.hour ?? 0,
      timeZone: transformTimezoneToApi(reportSchedule.timezone),
      expirationDate: reportSchedule.endDate
    }
  };
};

const buildWeeklySchedule = (reportSchedule: ReportSchedule): ReportConfigurationSchedule => {
  if (!reportSchedule.timezone) {
    throw new Error('Timezone is not set for Weekly Schedule');
  }
  if (!reportSchedule.endDate) {
    throw new Error('End Date is not set for Weekly Schedule');
  }
  if (!reportSchedule.daysOfWeek || reportSchedule.daysOfWeek.length === 0) {
    throw new Error('Days of Week is not set for Weekly Schedule');
  }

  return {
    recurringWeeklySchedule: {
      hourOfDay: reportSchedule.hour ?? 0,
      timeZone: transformTimezoneToApi(reportSchedule.timezone),
      expirationDate: reportSchedule.endDate,
      daysOfWeek: reportSchedule.daysOfWeek
    }
  };
};

const buildMonthlySchedule = (reportSchedule: ReportSchedule): ReportConfigurationSchedule => {
  if (!reportSchedule.timezone) {
    throw new Error('Timezone is not set for Monthly Schedule');
  }
  if (!reportSchedule.endDate) {
    throw new Error('End Date is not set for Monthly Schedule');
  }
  if (reportSchedule.lastDayOfMonth === true) {
    return {
      recurringMonthlyPresetSchedule: {
        hourOfDay: reportSchedule.hour ?? 0,
        timeZone: transformTimezoneToApi(reportSchedule.timezone),
        expirationDate: reportSchedule.endDate,
        preset: LAST_DAY_OF_MONTH_PRESET
      }
    };
  }
  if (!reportSchedule.dayOfMonth) {
    throw new Error('Day of Month is not set for Monthly Schedule');
  }
  return {
    recurringMonthlySchedule: {
      hourOfDay: reportSchedule.hour ?? 0,
      timeZone: transformTimezoneToApi(reportSchedule.timezone),
      expirationDate: reportSchedule.endDate,
      dayOfMonth: reportSchedule.dayOfMonth
    }
  };
};

export const buildReportDelivery = (delivery: ReportDelivery): ReportDeliverySettings | undefined => {
  if (delivery.emails && delivery.emails.length > 0) {
    return {
      email: delivery?.emails
    };
  }
  return undefined;
};

const buildListReportFilter = (filters: Record<string, any>, columnId: string) => {
  if (!filters[columnId]) {
    return undefined;
  }
  if (VirtualColId.QuickFilterVirtualColumnId === columnId) {
    if (typeof filters[columnId].filter !== 'string') {
      throw new Error('Invalid type of value in name filter');
    }
    return { include: [filters[columnId].filter] };
  } else if (SUBSCRIPTION_TABLE_COLUMN_ID.FREQUENCY === columnId) {
    return buildFrequencyFilter(filters[columnId].values);
  } else if (SUBSCRIPTION_TABLE_COLUMN_ID.FILE_FORMAT === columnId) {
    return buildFileFormatFilter(filters[columnId].values);
  } else {
    if (!isStringArray(filters[columnId].values)) {
      throw new Error('Invalid Status filter values');
    }
    return { include: filters[columnId].values };
  }
};

const buildLinkedAccountsFilter = ({
  managerAccounts,
  advertiserAccounts
}: {
  managerAccounts?: string[];
  advertiserAccounts?: string[];
}): { linkedAccountsFilter?: AccountIdFilter } => {
  const managerAccountFilter: AccountId[] = (managerAccounts ?? []).map(managerAccountId => ({ managerAccountId }));
  const advertiserAccountFilter: AccountId[] = (advertiserAccounts ?? []).map(advertiserAccountId => ({ advertiserAccountId }));

  if (managerAccountFilter.length === 0 && advertiserAccountFilter.length === 0) {
    return {};
  }

  return {
    linkedAccountsFilter: {
      include: [...managerAccountFilter, ...advertiserAccountFilter]
    }
  };
};

function isStringArray(value: any): value is string[] {
  return Array.isArray(value) && value.every(item => typeof item === 'string');
}

const buildFrequencyFilter = (values: string[]) => {
  if (!isStringArray(values)) {
    throw new Error('Invalid Frequency filter values');
  }
  const frequencyList: string[] = [];
  values.forEach(value => {
    if (value === REPORT_FREQUENCY.ONCE) {
      frequencyList.push(ScheduleType.NOW);
      frequencyList.push(ScheduleType.FUTURE);
    } else if (value === REPORT_FREQUENCY.DAILY) {
      frequencyList.push(ScheduleType.RECURRING_DAILY);
    } else if (value === REPORT_FREQUENCY.WEEKLY) {
      frequencyList.push(ScheduleType.RECURRING_WEEKLY);
    } else if (value === REPORT_FREQUENCY.MONTHLY) {
      frequencyList.push(ScheduleType.RECURRING_MONTHLY);
    }
  });
  return { include: frequencyList };
};

const buildFileFormatFilter = (values: string[]) => {
  if (!isStringArray(values)) {
    throw new Error('Invalid Format filter values');
  }
  const formatList: string[] = [];
  values.forEach(value => {
    if (value === REPORT_FORMAT.CSV) {
      formatList.push(ReportFormat.CSV);
    } else if (value === REPORT_FORMAT.EXCEL) {
      formatList.push(ReportFormat.XLSX);
    }
  });
  return { include: formatList };
};

const buildSort = (sort?: Sort | null) => {
  if (!sort || !sortFieldToApiMap[sort.columnName] || !sortDirectionToApiMap[sort.order]) {
    return undefined;
  }
  return [
    {
      by: sortFieldToApiMap[sort.columnName as SUBSCRIPTION_TABLE_COLUMN_ID],
      direction: sortDirectionToApiMap[sort.order]
    }
  ];
};

export const buildListFlexibleReportConfigurationsRequest = (
  pageSize: number,
  sort?: Sort | null,
  filterModel?: { [key: string]: any },
  accounts?: { managerAccounts?: string[]; advertiserAccounts?: string[] },
  nextToken?: string
) => {
  if (pageSize <= 0) {
    throw new Error(`Invalid pageSize value ${pageSize}. Must be greater than 0.`);
  }
  const filterGroup = filterModel ? (filterModel as Record<string, any>) : {};
  return {
    maxResults: pageSize,
    sort: buildSort(sort),
    nameFilter: buildListReportFilter(filterGroup, VirtualColId.QuickFilterVirtualColumnId),
    statusFilter: buildListReportFilter(filterGroup, SUBSCRIPTION_TABLE_COLUMN_ID.STATE),
    scheduleTypeFilter: buildListReportFilter(filterGroup, SUBSCRIPTION_TABLE_COLUMN_ID.FREQUENCY),
    formatFilter: buildListReportFilter(filterGroup, SUBSCRIPTION_TABLE_COLUMN_ID.FILE_FORMAT),
    ...buildLinkedAccountsFilter(accounts || {}),
    nextToken: nextToken
  } as ListFlexibleReportConfigurationsRequest;
};
